/**
 * Prompt library domain types.
 *
 * Shared by `services/storage/promptRepository.ts`, `stores/promptStore.ts`
 * and `PromptLibraryPage`. A prompt's `body` may contain `{{variable}}`
 * placeholders; `variables` lists their names so the UI can prompt for
 * values before inserting into the composer.
 */

export interface PromptCategory {
  readonly id: string;
  workspaceId: string;
  name: string;
  readonly createdAt: number;
}

export interface Prompt {
  readonly id: string;
  workspaceId: string;
  categoryId: string | null;
  title: string;
  body: string;
  /** Placeholder names extracted from `{{name}}` tokens in `body`. */
  variables: string[];
  isFavorite: boolean;
  readonly createdAt: number;
  updatedAt: number;
}

/** The shape written/read by prompt import/export — a portable subset that
 * excludes ids and workspace association so prompts can move between
 * workspaces or installs. */
export interface PromptExport {
  title: string;
  body: string;
  category?: string;
}

/** Extracts `{{name}}` placeholder names from a prompt body, in order of
 * first appearance, de-duplicated. */
export function extractVariables(body: string): string[] {
  const seen = new Set<string>();
  const matches = body.matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g);
  for (const match of matches) {
    const name = match[1];
    if (name) seen.add(name);
  }
  return [...seen];
}

/** Substitutes `{{name}}` tokens in `body` with the given values; any
 * variable left unfilled is emitted as-is so the user notices it. */
export function fillVariables(body: string, values: Record<string, string>): string {
  return body.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match: string, name: string): string =>
    Object.prototype.hasOwnProperty.call(values, name) ? values[name]! : match,
  );
}
